import { useRef, useCallback, useEffect } from 'react';
import { UploadClient } from '@uploadcare/upload-client';
import swal from 'sweetalert';

const EVIDENCE_COOLDOWN = 3000;

const uploadClient = new UploadClient({ publicKey: '680e67f638cface3e452' });

function dataURLtoFile(dataUrl, fileName) {
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], fileName, { type: mime });
}

const useObjectDetection = (videoRef, isActive, onViolation) => {
  const lastEvidenceRef = useRef({});
  const noFaceShownRef = useRef(false);
  const activeRef = useRef(isActive);
  activeRef.current = isActive;

  const captureScreenshot = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState !== 4 || video.videoWidth === 0) return null;

    const c = document.createElement('canvas');
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    const ctx = c.getContext('2d');
    ctx.drawImage(video, 0, 0, c.width, c.height);

    return c.toDataURL('image/jpeg', 0.85);
  }, [videoRef]);

  const captureAndUploadEvidence = useCallback(async (type) => {
    const now = Date.now();
    const last = lastEvidenceRef.current[type] || 0;
    if (now - last < EVIDENCE_COOLDOWN) return null;
    lastEvidenceRef.current[type] = now;

    const dataUrl = await captureScreenshot();
    if (!dataUrl) return null;

    try {
      const file = dataURLtoFile(dataUrl, `violation_${type}_${Date.now()}.jpg`);
      const result = await uploadClient.uploadFile(file);
      return result.cdnUrl;
    } catch (err) {
      console.error('[Uploadcare evidence upload failed]', err.message);
      return null;
    }
  }, [captureScreenshot]);

  const handleViolation = useCallback(async (type) => {
    const screenshotUrl = await captureAndUploadEvidence(type);

    if (onViolation) {
      onViolation(type, screenshotUrl);
    }

    switch (type) {
      case 'CELL_PHONE_DETECTED':
        await swal('Cell Phone Detected', 'Please remove any mobile devices. This has been recorded with evidence.', 'warning');
        break;
      case 'PROHIBITED_OBJECT':
        await swal('Prohibited Object Detected', 'Books, laptops, and other materials are not allowed. This has been recorded with evidence.', 'warning');
        break;
      case 'MULTIPLE_FACES':
        await swal('Multiple Faces Detected', 'Only one person should be visible. This has been recorded with evidence.', 'warning');
        break;
      case 'FACE_CHANGED':
        await swal('Face Changed', 'The person in the camera appears different. This has been recorded with evidence.', 'warning');
        break;
      case 'NO_FACE':
        if (!noFaceShownRef.current) {
          noFaceShownRef.current = true;
          await swal('Face Not Visible', 'Your face is not visible. Please position your face in the camera frame to continue the exam.', 'warning');
        }
        break;
      case 'TAB_SWITCH':
        await swal({
          title: 'Exam Window Violation',
          text: 'You left the exam window. Your face must be visible to continue the exam.',
          icon: 'warning',
          buttons: ['Cancel', 'Show My Face'],
          dangerMode: true,
        });
        break;
      case 'WINDOW_BLUR':
        await swal({
          title: 'Exam Window Lost Focus',
          text: 'The exam window lost focus. Your face must remain visible to continue. Please click back into the exam window.',
          icon: 'warning',
          buttons: ['OK'],
          dangerMode: true,
        });
        break;
      case 'CAMERA_COVERED':
        await swal({
          title: 'Camera Covered',
          text: 'Your camera appears to be covered or blocked. Your face must be clearly visible at all times during the exam. This incident has been recorded.',
          icon: 'warning',
          buttons: ['I Understand'],
          dangerMode: true,
        });
        break;
      default:
        break;
    }
  }, [captureAndUploadEvidence, onViolation]);

  const resetNoFaceWarning = useCallback(() => {
    noFaceShownRef.current = false;
  }, []);

  useEffect(() => {
    if (!isActive) return;
    activeRef.current = true;
  }, [isActive]);

  return { handleViolation, resetNoFaceWarning };
};

export default useObjectDetection;
